import { t } from "i18next";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getEnvVariables, getPods } from "src/api/api";
import { useAuth } from "src/hooks/use-auth";
import { Deployments, Status } from "src/types/deployments";

interface EnvVarsContextType {
  nsEnvVars: Record<string, string>;
  nsStatus: string;
  librelinkEnvVars: Record<string, string>;
  librelinkStatus: Status;
  fullUrl: string;
  pods: any[];
  isLoading: boolean;
  error: Error | null;
  border: { low: number; high: number };
  multiplier: number;
  refretchLibreLinkData: () => Promise<void>;
}

export type Deployment = {
  id: string;
  start_datetime: string;
  deployment: string;
  namespace: string;
  image: string;
  env_vars: {
    NIGHTSCOUT_URL: string;
    NIGHTSCOUT_API_TOKEN: string;
    LINK_UP_TIME_INTERVAL: string;
    LINK_UP_USERNAME: string;
    LINK_UP_PASSWORD: string;
  };
};

export type Border = { low: number; high: number };

const EnvVarsContext = createContext<EnvVarsContextType | undefined>(undefined);

export const EnvVarsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [data, setData] = useState<
    Omit<
      EnvVarsContextType,
      "border" | "multiplier" | "refretchLibreLinkData"
    >
  >({
    nsEnvVars: {},
    nsStatus: "",
    librelinkEnvVars: {},
    librelinkStatus: Status.INACTIVE,
    fullUrl: "",
    pods: [],
    isLoading: true,
    error: null,
  });

  // Use ref to track if we're currently refreshing to avoid multiple simultaneous calls
  const isRefreshingRef = useRef(false);

  const fetchLibreLinkData = useCallback(async () => {
    if (!user || isRefreshingRef.current) return;

    isRefreshingRef.current = true;

    try {
      const [librelinkEnvVars, podsResult] = await Promise.all([
        getEnvVariables(user, Deployments.LIBRELINK),
        getPods(user),
      ]);

      setData((prevData) => ({
        ...prevData,
        librelinkEnvVars: librelinkEnvVars.envVars ?? prevData.librelinkEnvVars,
        librelinkStatus: librelinkEnvVars.status,
        pods: podsResult.pods,
      }));
    } catch (err) {
      console.error("Failed to refresh LibreLink data:", err);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [user]);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setData((d) => ({ ...d, isLoading: false }));
      return;
    }

    setData((d) => ({ ...d, isLoading: true, error: null }));

    try {
      const [nsEnvVars, librelinkEnvVars, podsResult] = await Promise.all([
        getEnvVariables(user, Deployments.NIGHTSCOUT),
        getEnvVariables(user, Deployments.LIBRELINK),
        getPods(user),
      ]);

      if (!user?.nsInstance?.validated)
        throw new Error(t("UserNSInstanceNotFound"));

      const latestDeployment = user?.deployments?.sort(
        (a: Deployment, b: Deployment) =>
          new Date(b.start_datetime).getTime() -
          new Date(a.start_datetime).getTime()
      )[0];
      const libreLinkUpDefaultValues = {
        LINK_UP_USERNAME: latestDeployment?.env_vars?.LINK_UP_USERNAME || "",
        LINK_UP_PASSWORD: latestDeployment?.env_vars?.LINK_UP_PASSWORD || "",
        NIGHTSCOUT_URL:
          latestDeployment?.env_vars?.NIGHTSCOUT_URL ||
          user?.nsInstance?.url.toLowerCase(),
        NIGHTSCOUT_API_TOKEN:
          latestDeployment?.env_vars?.NIGHTSCOUT_API_TOKEN ||
          nsEnvVars?.envVars?.ACCESS_TOKEN,
        LINK_UP_TIME_INTERVAL:
          latestDeployment?.env_vars?.LINK_UP_TIME_INTERVAL || "5",
      };

      setData({
        nsEnvVars: nsEnvVars.envVars,
        nsStatus: podsResult.status,
        librelinkEnvVars: librelinkEnvVars.envVars ?? libreLinkUpDefaultValues,
        librelinkStatus: librelinkEnvVars.status,
        fullUrl: user.nsInstance.url,
        pods: podsResult.pods,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      console.error("Failed to fetch environment data:", err);
      setData((d) => ({
        ...d,
        isLoading: false,
        error:
          err instanceof Error
            ? err
            : new Error("Unknown error fetching environment data"),
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const { nsEnvVars } = data;

  // ✅ Derived values are memoized
  const displayUnits =
    nsEnvVars?.DISPLAY_UNITS === "mg/dl" ? "mg/dl" : "mmol/L";
  const multiplier = displayUnits === "mg/dl" ? 18 : 1;

  const border: Border = useMemo(() => {
    const low = nsEnvVars?.BG_TARGET_BOTTOM
      ? parseFloat(nsEnvVars?.BG_TARGET_BOTTOM)
      : displayUnits === "mg/dl"
      ? 80
      : 4.0;
    const high = nsEnvVars?.BG_TARGET_TOP
      ? parseFloat(nsEnvVars?.BG_TARGET_TOP)
      : displayUnits === "mg/dl"
      ? 180
      : 10;
    return { low, high };
  }, [nsEnvVars, displayUnits]);

  return (
    <EnvVarsContext.Provider
      value={{
        ...data,
        border,
        multiplier,
        refretchLibreLinkData: fetchLibreLinkData,
      }}
    >
      {!data.isLoading && children}
    </EnvVarsContext.Provider>
  );
};

export const useEnvVars = () => {
  const ctx = useContext(EnvVarsContext);
  if (!ctx)
    throw new Error("useEnvVars must be used within an EnvVarsProvider");
  return ctx;
};
